// Static theme color palettes - Source of truth for all theme colors
// These are HEX values that will be applied as inline styles in ThemePreview

export interface ThemePalette {
  light: {
    background: string;
    surface: string;
    surfaceAlt: string;
    primary: string;
    primaryText: string;
    secondary: string;
    secondaryText: string;
    accent: string;
    accentText: string;
    textPrimary: string;
    textSecondary: string;
    border: string;
    navBackground: string;
    navActive: string;
    navHover: string;
    navDefault: string;
    progressTrack: string;
    progressFill: string;
    cardHeaderBackground: string;
    cardHeaderText: string;
    focusRing: string;
    badgePrimary: string;
    badgePrimaryText: string;
    badgeSecondary: string;
    badgeSecondaryText: string;
    linkColor: string;
    linkHoverColor: string;
    inputBorder: string;
    inputFocusBorder: string;
    headerStripe?: string;
    outlineButton?: string;
  };
  dark: {
    background: string;
    surface: string;
    surfaceAlt: string;
    primary: string;
    primaryText: string;
    secondary: string;
    secondaryText: string;
    accent: string;
    accentText: string;
    textPrimary: string;
    textSecondary: string;
    border: string;
    navBackground: string;
    navActive: string;
    navHover: string;
    navDefault: string;
    progressTrack: string;
    progressFill: string;
    cardHeaderBackground: string;
    cardHeaderText: string;
    focusRing: string;
    badgePrimary: string;
    badgePrimaryText: string;
    badgeSecondary: string;
    badgeSecondaryText: string;
    linkColor: string;
    linkHoverColor: string;
    inputBorder: string;
    inputFocusBorder: string;
    headerStripe?: string;
    outlineButton?: string;
  };
}

export const THEME_PALETTES: Record<string, ThemePalette> = {
  default: {
    light: {
      background: '#F9FAFB',
      surface: '#FFFFFF',
      surfaceAlt: '#F3F4F6',
      primary: '#1E40AF',
      primaryText: '#FFFFFF',
      secondary: '#6B7280',
      secondaryText: '#FFFFFF',
      accent: '#10B981',
      accentText: '#FFFFFF',
      textPrimary: '#111827',
      textSecondary: '#6B7280',
      border: '#E5E7EB',
      navBackground: '#1E40AF',
      navActive: '#FFFFFF',
      navHover: '#DBEAFE',
      navDefault: '#93C5FD',
      progressTrack: '#E5E7EB',
      progressFill: '#1E40AF',
      cardHeaderBackground: '#F3F4F6',
      cardHeaderText: '#111827',
      focusRing: '#3B82F6',
      badgePrimary: '#1E40AF',
      badgePrimaryText: '#FFFFFF',
      badgeSecondary: '#10B981',
      badgeSecondaryText: '#FFFFFF',
      linkColor: '#1E40AF',
      linkHoverColor: '#1E3A8A',
      inputBorder: '#E5E7EB',
      inputFocusBorder: '#3B82F6',
    },
    dark: {
      background: '#0F172A',
      surface: '#1E293B',
      surfaceAlt: '#334155',
      primary: '#3B82F6',
      primaryText: '#0F172A',
      secondary: '#94A3B8',
      secondaryText: '#0F172A',
      accent: '#10B981',
      accentText: '#FFFFFF',
      textPrimary: '#F1F5F9',
      textSecondary: '#94A3B8',
      border: '#334155',
      navBackground: '#0F172A',
      navActive: '#3B82F6',
      navHover: '#1E3A8A',
      navDefault: '#475569',
      progressTrack: '#334155',
      progressFill: '#3B82F6',
      cardHeaderBackground: '#334155',
      cardHeaderText: '#F1F5F9',
      focusRing: '#60A5FA',
      badgePrimary: '#3B82F6',
      badgePrimaryText: '#0F172A',
      badgeSecondary: '#10B981',
      badgeSecondaryText: '#FFFFFF',
      linkColor: '#3B82F6',
      linkHoverColor: '#60A5FA',
      inputBorder: '#334155',
      inputFocusBorder: '#3B82F6',
    },
  },
  
  army: {
    light: {
      background: '#FCF6EB',
      surface: '#FFFFFF',
      surfaceAlt: '#F1E4C7',
      primary: '#000000',
      primaryText: '#FFFFFF',
      secondary: '#C8B896',
      secondaryText: '#000000',
      accent: '#FFCC01',
      accentText: '#000000',
      textPrimary: '#000000',
      textSecondary: '#4B5563',
      border: '#E5E5E5',
      navBackground: '#1A1A1A',
      navActive: '#FFCC01',
      navHover: '#C8B896',
      navDefault: '#6B7280',
      progressTrack: '#E5E5E5',
      progressFill: '#FFCC01',
      cardHeaderBackground: '#000000',
      cardHeaderText: '#FFCC01',
      focusRing: '#FFCC01',
      badgePrimary: '#FFCC01',
      badgePrimaryText: '#000000',
      badgeSecondary: '#C8B896',
      badgeSecondaryText: '#000000',
      linkColor: '#FFCC01',
      linkHoverColor: '#FFD740',
      inputBorder: '#E5E5E5',
      inputFocusBorder: '#FFCC01',
    },
    dark: {
      background: '#221F20',
      surface: '#1A2C35',
      surfaceAlt: '#2E3E46',
      primary: '#FFCC01',
      primaryText: '#221F20',
      secondary: '#1A2C35',
      secondaryText: '#FFFFFF',
      accent: '#FFCC01',
      accentText: '#221F20',
      textPrimary: '#FFFFFF',
      textSecondary: '#666666',
      border: '#2E3E46',
      navBackground: '#000000',
      navActive: '#FFCC01',
      navHover: '#FFCC01',
      navDefault: '#666666',
      progressTrack: '#2E3E46',
      progressFill: '#FFCC01',
      cardHeaderBackground: '#FFCC01',
      cardHeaderText: '#221F20',
      focusRing: '#FFCC01',
      badgePrimary: '#FFCC01',
      badgePrimaryText: '#221F20',
      badgeSecondary: '#2E3E46',
      badgeSecondaryText: '#FFFFFF',
      linkColor: '#FFCC01',
      linkHoverColor: '#FFD740',
      inputBorder: '#2E3E46',
      inputFocusBorder: '#FFCC01',
    },
  },
  
  navy: {
    light: {
      background: '#F0F7FC',
      surface: '#FFFFFF',
      surfaceAlt: '#F8FAFC',
      primary: '#002F6C',
      primaryText: '#FFFFFF',
      secondary: '#0076A9',
      secondaryText: '#FFFFFF',
      accent: '#E8B00F',
      accentText: '#002F6C',
      textPrimary: '#002F6C',
      textSecondary: '#4B5563',
      border: '#E5E5E5',
      navBackground: '#001A33',
      navActive: '#0076A9',
      navHover: '#003366',
      navDefault: '#4A5568',
      progressTrack: '#E5E5E5',
      progressFill: '#0076A9',
      cardHeaderBackground: '#003366',
      cardHeaderText: '#FFFFFF',
      focusRing: '#E8B00F',
      badgePrimary: '#002F6C',
      badgePrimaryText: '#FFFFFF',
      badgeSecondary: '#E8B00F',
      badgeSecondaryText: '#002F6C',
      linkColor: '#0076A9',
      linkHoverColor: '#002F6C',
      inputBorder: '#E5E5E5',
      inputFocusBorder: '#E8B00F',
    },
    dark: {
      background: '#010D10',
      surface: '#102534',
      surfaceAlt: '#1A2C35',
      primary: '#0076A9',
      primaryText: '#FFFFFF',
      secondary: '#022A3A',
      secondaryText: '#FFFFFF',
      accent: '#E8B00F',
      accentText: '#010D10',
      textPrimary: '#FFFFFF',
      textSecondary: '#C5CFDA',
      border: '#2E3E46',
      navBackground: '#000D14',
      navActive: '#0076A9',
      navHover: '#0076A9',
      navDefault: '#6B7280',
      progressTrack: '#2E3E46',
      progressFill: '#0076A9',
      cardHeaderBackground: '#0076A9',
      cardHeaderText: '#FFFFFF',
      focusRing: '#E8B00F',
      badgePrimary: '#0076A9',
      badgePrimaryText: '#FFFFFF',
      badgeSecondary: '#E8B00F',
      badgeSecondaryText: '#010D10',
      linkColor: '#0076A9',
      linkHoverColor: '#E8B00F',
      inputBorder: '#2E3E46',
      inputFocusBorder: '#E8B00F',
    },
  },
  
  marines: {
    light: {
      background: '#FFFAF8',
      surface: '#FFFFFF',
      surfaceAlt: '#F8F8F8',
      primary: '#A50034',
      primaryText: '#FFFFFF',
      secondary: '#73859F',
      secondaryText: '#FFFFFF',
      accent: '#B00D25',
      accentText: '#FFFFFF',
      textPrimary: '#121212',
      textSecondary: '#4B5563',
      border: '#E5E5E5',
      navBackground: '#1A1A1A',
      navActive: '#FFFFFF',
      navHover: '#A50034',
      navDefault: '#6B7280',
      progressTrack: '#E5E5E5',
      progressFill: '#A50034',
      cardHeaderBackground: '#A50034',
      cardHeaderText: '#FFFFFF',
      focusRing: '#A50034',
      badgePrimary: '#A50034',
      badgePrimaryText: '#FFFFFF',
      badgeSecondary: '#73859F',
      badgeSecondaryText: '#FFFFFF',
      linkColor: '#A50034',
      linkHoverColor: '#B00D25',
      inputBorder: '#E5E5E5',
      inputFocusBorder: '#A50034',
      headerStripe: '#B00D25',
    },
    dark: {
      background: '#151B17',
      surface: '#1A2C35',
      surfaceAlt: '#2E3E46',
      primary: '#FFFFFF',
      primaryText: '#151B17',
      secondary: '#73859F',
      secondaryText: '#FFFFFF',
      accent: '#B00D25',
      accentText: '#FFFFFF',
      textPrimary: '#FFFFFF',
      textSecondary: '#777777',
      border: '#2E3E46',
      navBackground: '#000013',
      navActive: '#FFFFFF',
      navHover: '#B00D25',
      navDefault: '#6B7280',
      progressTrack: '#2B333F',
      progressFill: '#FFFFFF',
      cardHeaderBackground: '#FFFFFF',
      cardHeaderText: '#151B17',
      focusRing: '#FFFFFF',
      badgePrimary: '#FFFFFF',
      badgePrimaryText: '#151B17',
      badgeSecondary: '#B00D25',
      badgeSecondaryText: '#FFFFFF',
      linkColor: '#FFFFFF',
      linkHoverColor: '#B00D25',
      inputBorder: '#2E3E46',
      inputFocusBorder: '#FFFFFF',
      headerStripe: '#B00D25',
    },
  },
  
  airforce: {
    light: {
      background: '#F5F8FB',
      surface: '#FFFFFF',
      surfaceAlt: '#F8FAFC',
      primary: '#00308F',
      primaryText: '#FFFFFF',
      secondary: '#0B4F75',
      secondaryText: '#FFFFFF',
      accent: '#628CBF',
      accentText: '#FFFFFF',
      textPrimary: '#00308F',
      textSecondary: '#4B5563',
      border: '#E5E5E5',
      navBackground: '#0A1628',
      navActive: '#FFFFFF',
      navHover: '#4978B2',
      navDefault: '#6B7280',
      progressTrack: '#E5E5E5',
      progressFill: '#4978B2',
      cardHeaderBackground: '#1B2838',
      cardHeaderText: '#FFFFFF',
      focusRing: '#4978B2',
      badgePrimary: '#00308F',
      badgePrimaryText: '#FFFFFF',
      badgeSecondary: '#628CBF',
      badgeSecondaryText: '#FFFFFF',
      linkColor: '#4978B2',
      linkHoverColor: '#00308F',
      inputBorder: '#E5E5E5',
      inputFocusBorder: '#4978B2',
    },
    dark: {
      background: '#0E172D',
      surface: '#242C43',
      surfaceAlt: '#2E3E46',
      primary: '#4978B2',
      primaryText: '#FFFFFF',
      secondary: '#0B4F75',
      secondaryText: '#FFFFFF',
      accent: '#628CBF',
      accentText: '#FFFFFF',
      textPrimary: '#FFFFFF',
      textSecondary: '#C5CFDA',
      border: '#2E3E46',
      navBackground: '#000000',
      navActive: '#4978B2',
      navHover: '#4978B2',
      navDefault: '#6B7280',
      progressTrack: '#2B4B5C',
      progressFill: '#4978B2',
      cardHeaderBackground: '#4978B2',
      cardHeaderText: '#FFFFFF',
      focusRing: '#4978B2',
      badgePrimary: '#4978B2',
      badgePrimaryText: '#FFFFFF',
      badgeSecondary: '#628CBF',
      badgeSecondaryText: '#FFFFFF',
      linkColor: '#4978B2',
      linkHoverColor: '#628CBF',
      inputBorder: '#2E3E46',
      inputFocusBorder: '#4978B2',
    },
  },
  
  spaceforce: {
    light: {
      background: '#F8F9FA',
      surface: '#FFFFFF',
      surfaceAlt: '#F8FAFC',
      primary: '#1A1F36',
      primaryText: '#FFFFFF',
      secondary: '#C0C0C0',
      secondaryText: '#1A1F36',
      accent: '#FFD336',
      accentText: '#1A1F36',
      textPrimary: '#1A1F36',
      textSecondary: '#4B5563',
      border: '#E5E5E5',
      navBackground: '#0B1120',
      navActive: '#E34217',
      navHover: '#FFD336',
      navDefault: '#6B7280',
      progressTrack: '#E5E5E5',
      progressFill: '#E34217',
      cardHeaderBackground: '#E34217',
      cardHeaderText: '#FFFFFF',
      focusRing: '#E34217',
      badgePrimary: '#1A1F36',
      badgePrimaryText: '#FFFFFF',
      badgeSecondary: '#FFD336',
      badgeSecondaryText: '#000000',
      linkColor: '#E34217',
      linkHoverColor: '#D0411A',
      inputBorder: '#E5E5E5',
      inputFocusBorder: '#E34217',
      outlineButton: '#E34217',
    },
    dark: {
      background: '#000000',
      surface: '#1A2C35',
      surfaceAlt: '#2E3E46',
      primary: '#E34217',
      primaryText: '#FFFFFF',
      secondary: '#D0411A',
      secondaryText: '#FFFFFF',
      accent: '#FFD336',
      accentText: '#000000',
      textPrimary: '#FFFFFF',
      textSecondary: '#C5CFDA',
      border: '#2E3E46',
      navBackground: '#000000',
      navActive: '#E34217',
      navHover: '#FFD336',
      navDefault: '#6B7280',
      progressTrack: '#2E3E46',
      progressFill: '#E34217',
      cardHeaderBackground: '#E34217',
      cardHeaderText: '#FFFFFF',
      focusRing: '#E34217',
      badgePrimary: '#E34217',
      badgePrimaryText: '#FFFFFF',
      badgeSecondary: '#FFD336',
      badgeSecondaryText: '#000000',
      linkColor: '#E34217',
      linkHoverColor: '#FFD336',
      inputBorder: '#2E3E46',
      inputFocusBorder: '#E34217',
      outlineButton: '#E34217',
    },
  },
  
  coastguard: {
    light: {
      background: '#F5FAFF',
      surface: '#FFFFFF',
      surfaceAlt: '#F8FAFC',
      primary: '#003366',
      primaryText: '#FFFFFF',
      secondary: '#E1251B',
      secondaryText: '#FFFFFF',
      accent: '#006BA6',
      accentText: '#FFFFFF',
      textPrimary: '#003366',
      textSecondary: '#4B5563',
      border: '#E5E5E5',
      navBackground: '#001A33',
      navActive: '#FFFFFF',
      navHover: '#006BA6',
      navDefault: '#6B7280',
      progressTrack: '#E5E5E5',
      progressFill: '#006BA6',
      cardHeaderBackground: '#003366',
      cardHeaderText: '#FFFFFF',
      focusRing: '#006BA6',
      badgePrimary: '#006BA6',
      badgePrimaryText: '#FFFFFF',
      badgeSecondary: '#0B4F75',
      badgeSecondaryText: '#FFFFFF',
      linkColor: '#006BA6',
      linkHoverColor: '#005CB9',
      inputBorder: '#2E3E46',
      inputFocusBorder: '#006BA6',
      headerStripe: '#E1251B',
    },
    dark: {
      background: '#001529',
      surface: '#1A2C35',
      surfaceAlt: '#2E3E46',
      primary: '#006BA6',
      primaryText: '#FFFFFF',
      secondary: '#0B4F75',
      secondaryText: '#FFFFFF',
      accent: '#005CB9',
      accentText: '#FFFFFF',
      textPrimary: '#FFFFFF',
      textSecondary: '#C5CFDA',
      border: '#2E3E46',
      navBackground: '#000000',
      navActive: '#FFFFFF',
      navHover: '#006BA6',
      navDefault: '#6B7280',
      progressTrack: '#2E3E46',
      progressFill: '#006BA6',
      cardHeaderBackground: '#006BA6',
      cardHeaderText: '#FFFFFF',
      focusRing: '#006BA6',
      badgePrimary: '#006BA6',
      badgePrimaryText: '#FFFFFF',
      badgeSecondary: '#0B4F75',
      badgeSecondaryText: '#FFFFFF',
      linkColor: '#006BA6',
      linkHoverColor: '#005CB9',
      inputBorder: '#2E3E46',
      inputFocusBorder: '#006BA6',
      headerStripe: '#E1251B',
    },
  },
  
  other: {
    light: {
      background: '#F9FAFB',
      surface: '#FFFFFF',
      surfaceAlt: '#F3F4F6',
      primary: '#4B5563',
      primaryText: '#FFFFFF',
      secondary: '#9CA3AF',
      secondaryText: '#111827',
      accent: '#6B7280',
      accentText: '#FFFFFF',
      textPrimary: '#111827',
      textSecondary: '#6B7280',
      border: '#E5E7EB',
      navBackground: '#4B5563',
      navActive: '#FFFFFF',
      navHover: '#6B7280',
      navDefault: '#9CA3AF',
      progressTrack: '#E5E7EB',
      progressFill: '#4B5563',
      cardHeaderBackground: '#4B5563',
      cardHeaderText: '#FFFFFF',
      focusRing: '#6B7280',
      badgePrimary: '#6B7280',
      badgePrimaryText: '#FFFFFF',
      badgeSecondary: '#9CA3AF',
      badgeSecondaryText: '#111827',
      linkColor: '#4B5563',
      linkHoverColor: '#374151',
      inputBorder: '#E5E7EB',
      inputFocusBorder: '#6B7280',
    },
    dark: {
      background: '#0F172A',
      surface: '#1E293B',
      surfaceAlt: '#334155',
      primary: '#CBD5E1',
      primaryText: '#0F172A',
      secondary: '#94A3B8',
      secondaryText: '#0F172A',
      accent: '#D1D5DB',
      accentText: '#0F172A',
      textPrimary: '#F1F5F9',
      textSecondary: '#94A3B8',
      border: '#334155',
      navBackground: '#0F172A',
      navActive: '#CBD5E1',
      navHover: '#475569',
      navDefault: '#374151',
      progressTrack: '#334155',
      progressFill: '#CBD5E1',
      cardHeaderBackground: '#334155',
      cardHeaderText: '#F1F5F9',
      focusRing: '#94A3B8',
      badgePrimary: '#475569',
      badgePrimaryText: '#F1F5F9',
      badgeSecondary: '#CBD5E1',
      badgeSecondaryText: '#0F172A',
      linkColor: '#CBD5E1',
      linkHoverColor: '#E2E8F0',
      inputBorder: '#334155',
      inputFocusBorder: '#94A3B8',
    },
  },
};
